import { Button, Card, Tag } from "@arco-design/web-react";
import { KeyRound, Pencil, Plus, UserCog } from "lucide-react";

import { sysDeptUserPageSizeOptions } from "../config";
import type { SysStaffItem } from "../types";
import "./SysDeptUserPage.css";

type SysStaffResultTableProps = {
  canManage: boolean;
  items: SysStaffItem[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  selectedDeptName: string;
  selectedDeptId?: number;
  onCreateStaff: () => void;
  onEditStaff: (item: SysStaffItem) => void;
  onResetPassword: (item: SysStaffItem) => void;
  onToggleStaffStatus: (item: SysStaffItem) => void;
  onMoveStaffToSelectedDept: (item: SysStaffItem) => void;
  onEditSupervisors: (item: SysStaffItem) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

/** 用户结果表格。 */
export function SysStaffResultTable({
  canManage,
  items,
  loading,
  page,
  pageSize,
  total,
  selectedDeptName,
  selectedDeptId,
  onCreateStaff,
  onEditStaff,
  onResetPassword,
  onToggleStaffStatus,
  onMoveStaffToSelectedDept,
  onEditSupervisors,
  onPageChange,
  onPageSizeChange,
}: SysStaffResultTableProps) {
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  return (
    <Card className="sys-dept-user-card" bodyStyle={{ padding: 0 }}>
      <div style={{ padding: 18 }}>
        <div className="sys-dept-user-results-header">
          <div>
            <h2>用户列表</h2>
            <div className="sys-dept-user-tree-meta">
              当前部门：{selectedDeptName || "全部用户"}
            </div>
          </div>
          {canManage ? (
            <Button shape="round" icon={<Plus size={16} />} onClick={onCreateStaff}>
              新增用户
            </Button>
          ) : null}
        </div>
      </div>
      <div className="sys-dept-user-table-scroll">
        <table className="sys-dept-user-table">
          <thead>
            <tr>
              <th>操作</th>
              <th>姓名</th>
              <th>身份</th>
              <th>账号</th>
              <th>手机号</th>
              <th>主部门</th>
              <th>归属部门</th>
              <th>领导</th>
              <th>状态</th>
              <th>更新时间</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="sys-dept-user-empty">
                  正在加载用户...
                </td>
              </tr>
            ) : null}
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={10} className="sys-dept-user-empty">
                  暂无用户数据
                </td>
              </tr>
            ) : null}
            {!loading
              ? items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="sys-dept-user-row-actions">
                        {canManage ? (
                          <Button
                            type="text"
                            shape="circle"
                            aria-label={`编辑用户 ${item.realname}`}
                            icon={<Pencil size={16} />}
                            onClick={() => onEditStaff(item)}
                          />
                        ) : null}
                        <Button
                          type="text"
                          shape="circle"
                          aria-label={`重置密码 ${item.realname || item.nickname || item.username}`}
                          icon={<KeyRound size={16} />}
                          onClick={() => onResetPassword(item)}
                        />
                        {canManage && item.isStaff ? (
                          <Button
                            type="text"
                            shape="circle"
                            aria-label={`添加领导 ${item.realname}`}
                            icon={<UserCog size={16} />}
                            onClick={() => onEditSupervisors(item)}
                          />
                        ) : null}
                        {canManage ? (
                          <Button type="text" onClick={() => onToggleStaffStatus(item)}>
                            {item.status === 1 ? "停用" : "启用"}
                          </Button>
                        ) : null}
                        {canManage && selectedDeptId && item.mainDeptId !== selectedDeptId ? (
                          <Button
                            type="text"
                            aria-label={`移动${item.realname || item.nickname || item.username}到${selectedDeptName}`}
                            onClick={() => onMoveStaffToSelectedDept(item)}
                          >
                            移动到当前部门
                          </Button>
                        ) : null}
                      </div>
                    </td>
                    <td>{item.realname || item.nickname || item.username}</td>
                    <td>
                      <div className="sys-dept-user-tag-stack">
                        <Tag color={item.isStaff ? "arcoblue" : "gray"}>{item.isStaff ? "员工" : "用户"}</Tag>
                        {item.isSuperuser ? <Tag color="gold">超管</Tag> : null}
                      </div>
                    </td>
                    <td>{item.username}</td>
                    <td>{item.phone}</td>
                    <td>{item.mainDeptName}</td>
                    <td>{item.deptNames.join("、")}</td>
                    <td>{item.supervisors.map((supervisor) => supervisor.supervisorName).join("、") || "-"}</td>
                    <td>
                      <Tag color={item.status === 1 ? "green" : "orangered"}>{item.statusLabel}</Tag>
                    </td>
                    <td>{item.updateTime}</td>
                  </tr>
                ))
              : null}
          </tbody>
        </table>
      </div>
      <div style={{ padding: 18 }}>
        <div className="sys-dept-user-pagination">
          <span>共 {total} 条</span>
          <Button disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            上一页
          </Button>
          <span>
            {page} / {totalPages}
          </span>
          <Button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            下一页
          </Button>
          <label className="sys-dept-user-field">
            <span>每页条数</span>
            <select
              aria-label="每页条数"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {sysDeptUserPageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option} 条
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </Card>
  );
}
